import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { useToast } from "@/hooks/use-toast"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Card } from "@/components/ui/card"
import { updateProfile } from "@/lib/queries/profile"
import { ISD_CODES } from "@/lib/constants"

interface AnonProfile {
  id: string
  full_name: string
  email: string
  phone?: string
  created_at?: string
  updated_at?: string
}

interface AnonProfileFormProps {
  initialData?: AnonProfile
  session: any;
  profile: any;
}

export const AnonProfileForm = ({ initialData, session, profile }: AnonProfileFormProps) => {
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<AnonProfile>({
    defaultValues: {
      id: initialData?.id || "",
      full_name: initialData?.full_name || "",
      email: initialData?.email || "",
      phone: initialData?.phone || "",
      created_at: initialData?.created_at || new Date().toISOString(),
      updated_at: initialData?.updated_at || new Date().toISOString(),
    }
  })

  const [loading, setLoading] = useState(false)
  const [selectedIsdCode, setSelectedIsdCode] = useState<string>("+1")
  const navigate = useNavigate()
  const { toast } = useToast()

  // Set initial values when initialData changes
  useEffect(() => {
    if (initialData) {
      setValue("id", initialData.id)
      setValue("full_name", initialData.full_name)
      setValue("email", initialData.email)
      setValue("phone", initialData.phone || "")
      setValue("created_at", initialData.created_at || new Date().toISOString())
      setValue("updated_at", initialData.updated_at || new Date().toISOString())
    }
  }, [initialData, setValue])

  const onSubmit = async (data: AnonProfile) => {
    try {
      setLoading(true)

      await updateProfile({
        id: data.id,
        full_name: data.full_name,
        email: data.email,
        phone: data.phone ? `${selectedIsdCode}${data.phone}` : null,
        isd_code: selectedIsdCode
      })

      toast({
        title: "Profile Updated",
        description: "Your profile has been updated successfully.",
      })

      // Refresh the page to show updated data
      window.location.reload()
    } catch (error: any) {
      console.error("Error updating profile:", error)
      toast({
        title: "Error",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label htmlFor="full_name">Full Name</Label>
          <Input
            id="full_name"
            {...register("full_name", { required: "Full name is required" })}
            className={errors.full_name ? "border-red-500" : ""}
          />
          {errors.full_name && (
            <p className="text-sm text-red-500 mt-1">{errors.full_name.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="email">Email Address</Label>
          <Input
            id="email"
            type="email"
            {...register("email", { required: "Email is required" })}
            className={errors.email ? "border-red-500" : ""}
          />
          {errors.email && (
            <p className="text-sm text-red-500 mt-1">{errors.email.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="phone">Phone Number (Optional)</Label>
          <div className="flex gap-2">
            <Select
              value={selectedIsdCode}
              onValueChange={setSelectedIsdCode}
            >
              <SelectTrigger className="w-[100px]">
                <SelectValue placeholder="Code" />
              </SelectTrigger>
              <SelectContent>
                {ISD_CODES.map((code) => (
                  <SelectItem key={code.value} value={code.value}>
                    {code.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              id="phone"
              type="tel"
              {...register("phone")}
              className={errors.phone ? "border-red-500" : ""}
            />
          </div>
          {errors.phone && (
            <p className="text-sm text-red-500 mt-1">{errors.phone.message}</p>
          )}
        </div>
      </div>

      <Card className="p-4 bg-blue-50 border-blue-200">
        <p className="text-sm text-blue-800">
          This is a temporary anonymous profile. To access more features and save your preferences permanently, consider creating a full account.
        </p>
      </Card>

      <div className="flex justify-end gap-4">
        <Button
          type="submit"
          disabled={loading}
        >
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </form>
  )
}
